import { decodeBase64Url, encodeBase64Url } from "@/helpers/UrlStateHelpers";
import { TableState, Updater } from "@tanstack/react-table";
import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

type Storage = "localStorage" | "url";

interface UseTableFiltersProps {
  tableName: string;
  state: Partial<TableState>;
  onStateChange: (updater: Updater<Partial<TableState>>) => void;
  storage?: Storage;
  defaults?: { [key: string]: any };
}

interface UseTableFiltersReturn {
  onFiltersChange: (filters: { [key: string]: any }) => void;
  clearFilters: () => void;
}

// Liste des clés à exclure (ne sont pas des filtres)
const NON_FILTER_KEYS = [
  "pagination",
  "columnOrder",
  "columnVisibility",
  "globalFilter",
  "sorting",
  "rowSelection",
];

const URL_PARAM_KEY = "f";

const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

export const useTableFilters = ({
  tableName,
  state,
  onStateChange,
  storage = "localStorage",
  defaults,
}: UseTableFiltersProps): UseTableFiltersReturn => {
  const [, setSearchParams] = useSearchParams();

  // LocalStorage helpers
  const setStoredFilters = useCallback(
    (filters: { [key: string]: any }): void => {
      localStorage.setItem(`${tableName}Filters`, JSON.stringify(filters));
    },
    [tableName]
  );

  const removeStoredFilters = useCallback((): void => {
    localStorage.removeItem(`${tableName}Filters`);
  }, [tableName]);

  // URL helpers
  const setUrlFilters = useCallback(
    (filters: { [key: string]: any }): void => {
      const encoded = encodeBase64Url(JSON.stringify(filters));
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(URL_PARAM_KEY, encoded);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const removeUrlFilters = useCallback((): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(URL_PARAM_KEY);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Extract filters from state (exclude non-filter keys, defaults and empty values)
  const extractFilters = useCallback(
    (tableState: Partial<TableState>): { [key: string]: any } => {
      const filters: { [key: string]: any } = {};
      Object.keys(tableState).forEach((key) => {
        if (NON_FILTER_KEYS.includes(key)) return;
        const value = (tableState as Record<string, any>)[key];
        if (value === null || value === undefined || value === "") return;
        if (Array.isArray(value) && value.length === 0) return;
        if (defaults && key in defaults && deepEqual(value, defaults[key])) return;
        filters[key] = value;
      });
      return filters;
    },
    [defaults]
  );

  // Persist filters whenever state changes
  useEffect(() => {
    const currentFilters = extractFilters(state);
    const hasFilters = Object.keys(currentFilters).length > 0;

    if (storage === "url") {
      if (hasFilters) setUrlFilters(currentFilters);
      else removeUrlFilters();
    } else {
      if (hasFilters) setStoredFilters(currentFilters);
      else removeStoredFilters();
    }
  }, [
    state,
    storage,
    extractFilters,
    setStoredFilters,
    removeStoredFilters,
    setUrlFilters,
    removeUrlFilters,
  ]);

  // Handle filter changes
  const onFiltersChange = useCallback(
    (filters: { [key: string]: any }) => {
      onStateChange((old) => {
        const result = {
          ...old,
          ...filters,
          pagination: {
            pageSize: old.pagination?.pageSize ?? 15,
            pageIndex: 0,
          },
        };
        return result;
      });
    },
    [onStateChange]
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    onStateChange((old) => {
      const result: Partial<TableState> = {
        pagination: old.pagination,
        columnOrder: old.columnOrder,
        columnVisibility: old.columnVisibility,
        globalFilter: old.globalFilter,
        sorting: old.sorting,
        rowSelection: old.rowSelection,
      };
      return result;
    });
    if (storage === "url") removeUrlFilters();
    else removeStoredFilters();
  }, [onStateChange, removeStoredFilters, removeUrlFilters, storage]);

  return {
    onFiltersChange,
    clearFilters,
  };
};

/**
 * Read initial filters synchronously from the current URL's query string
 * (param key: "f", base64-url encoded JSON). Pair with useTableFilters({ storage: "url" }).
 */
export const getInitialUrlState = (): { [key: string]: any } => {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(URL_PARAM_KEY);
    if (!raw) return {};
    return JSON.parse(decodeBase64Url(raw));
  } catch {
    return {};
  }
};
